import { useApi } from '@hero/context';
import type {
  UseRouteCreateApi,
  UseRouteDeleteApi,
  UseRouteGetOneApi,
  UseRouteListApi,
  UseRouteUpdateApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Get one Route by `_id`. Disabled while `id` is undefined.
 */
export const useRouteApi: UseRouteGetOneApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'route',
    apiFn: api.routes.getById,
    singleIdQuery: id,
    query: undefined,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * Paginated Route list. Filter by `sellerId`, `buyerId`, `agreementId`,
 * `isOwnRoute`, or `vehicle`. Server scopes to the caller's org by default.
 *
 * Hook API: {@link UseRouteListApi}
 */
export const useRouteListApi: UseRouteListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'route',
    apiFn: api.routes.getAll,
    query,
    apiConfig,
  });
};

/**
 * Create Route. Invalidates the `route` entity (which cascades to
 * `agreement` / `price` / `order` / `offer` caches per the entity config).
 *
 * Hook API: {@link UseRouteCreateApi}
 */
export const useRouteCreateApi: UseRouteCreateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'route',
    apiFn: api.routes.create,
    apiConfig,
  });
};

/**
 * Update Route. Partial body; server validates seller/buyer/vehicle rules.
 *
 * Hook API: {@link UseRouteUpdateApi}
 */
export const useRouteUpdateApi: UseRouteUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'route',
    apiFn: api.routes.update,
    apiConfig,
  });
};

/**
 * Delete Route. Only the seller / buyer party (or super admin) can delete.
 *
 * Hook API: {@link UseRouteDeleteApi}
 */
export const useRouteDeleteApi: UseRouteDeleteApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'route',
    apiFn: api.routes.delete,
    apiConfig,
  });
};
